import React, { useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import PDFDocument from './components/PDFDocument';
import CoverLetterDocument from './components/CoverLetterDocument';
import PersonalInfo from './components/PersonalInfo';
import Experience from './components/Experience';
import Education from './components/Education';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Languages from './components/Languages';
import Certifications from './components/Certifications';
import Preview from './components/Preview';
import CoverLetterForm from './components/CoverLetterForm';
import './App.css';
import cn from './assets/cn.png';
import { FiGlobe, FiX, FiFileText, FiGithub, FiLinkedin, FiMail, FiHeart } from 'react-icons/fi';

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [language, setLanguage] = useState('fr'); // 'fr' ou 'en'

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    linkedin: '',
    github: '',
    portfolio: '',
    photo: null,
    summary: '',
    education: [],
    experience: [],
    projects: [],
    skills: { programming: [], frameworks: [], databases: [], tools: [], dataAnalysis: [] },
    languages: [],
    certifications: []
  });

  const [coverLetterData, setCoverLetterData] = useState({
    companyName: '',
    positionTitle: '',
    hiringManager: '',
    letterBody: ''
  });

  // === TRADUCTIONS ===
  const t = {
    title: language === 'fr' ? 'Générateur de CV Professionnel' : 'Professional CV Generator',
    subtitle: language === 'fr' ? 'Créez votre CV by Manel SIDAOUI' : 'Create your CV by Manel SIDAOUI',
    about: language === 'fr' ? 'À propos' : 'About',
    coverLetter: language === 'fr' ? 'Lettre de motivation' : 'Cover Letter',
    close: language === 'fr' ? '×' : '×',
    engineer: language === 'fr' ? 'Ingénieure Informatique' : 'Computer Engineer',
    role: language === 'fr' ? 'Ingénieure Informatique | Développeuse Full-Stack' : 'Computer Engineer | Full-Stack Developer',
    aboutText: language === 'fr'
      ? 'Ingénieure INformatique, passionnée par le développement web, l\'analyse de données et l\'IA. Ce générateur de CV a été conçu pour aider les étudiants et jeunes diplômés à créer un CV professionnel, clair et moderne, inspiré de mon propre parcours.'
      : 'Computer Engineer, passionate about web development, data analysis, and AI. This CV generator was designed to help students and recent graduates create a professional, clear, and modern CV, inspired by my own journey.',
    linkedin: language === 'fr' ? 'LinkedIn' : 'LinkedIn',
    portfolio: language === 'fr' ? 'Portfolio' : 'Portfolio',
    email: language === 'fr' ? 'Email' : 'Email',
    previous: language === 'fr' ? 'Précédent' : 'Previous',
    next: language === 'fr' ? 'Suivant' : 'Next',
    download: language === 'fr' ? 'Télécharger le CV' : 'Download CV',
    downloadCoverLetter: language === 'fr' ? 'Télécharger la lettre' : 'Download Cover Letter',
    generating: language === 'fr' ? 'Génération...' : 'Generating...',
    alertName: language === 'fr' ? 'Veuillez remplir au moins le nom !' : 'Please fill in at least the name!',
    alertError: language === 'fr' ? 'Erreur lors de la génération' : 'Error generating PDF',
    steps: language === 'fr'
      ? ['Informations', 'Expérience', 'Formation', 'Projets', 'Compétences', 'Langues', 'Certifications', 'Aperçu']
      : ['Personal Info', 'Experience', 'Education', 'Projects', 'Skills', 'Languages', 'Certifications', 'Preview'],
    
    coverLetterTitle: language === 'fr' ? 'Lettre de motivation' : 'Cover Letter',
    companyName: language === 'fr' ? 'Nom de l\'entreprise' : 'Company Name',
    positionTitle: language === 'fr' ? 'Poste visé' : 'Target Position',
    hiringManager: language === 'fr' ? 'Destinataire' : 'Recipient',
    letterBody: language === 'fr' ? 'Contenu de la lettre' : 'Letter Content',
    coverLetterAlert: language === 'fr' ? 'Veuillez remplir les informations requises' : 'Please fill required information',
    
    // Nouvelles traductions pour le footer
    footerText: language === 'fr' ? 'Créé avec' : 'Made with',
    by: language === 'fr' ? 'par' : 'by',
    rights: language === 'fr' ? 'Tous droits réservés' : 'All rights reserved'
  };

  const steps = [
    { number: 1, title: t.steps[0], component: PersonalInfo },
    { number: 2, title: t.steps[1], component: Experience },
    { number: 3, title: t.steps[2], component: Education },
    { number: 4, title: t.steps[3], component: Projects },
    { number: 5, title: t.steps[4], component: Skills },
    { number: 6, title: t.steps[5], component: Languages },
    { number: 7, title: t.steps[6], component: Certifications },
    { number: 8, title: t.steps[7], component: Preview }
  ];

  const nextStep = () => currentStep < steps.length && setCurrentStep(currentStep + 1);
  const prevStep = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const handleDownload = async () => {
    if (!formData.name) {
      alert(t.alertName);
      return;
    }
    try {
      setIsGenerating(true);
      const blob = await pdf(<PDFDocument formData={formData} language={language} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CV_${formData.name.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert(t.alertError);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadCoverLetter = async () => {
    if (!coverLetterData.companyName || !coverLetterData.positionTitle) {
      alert(t.coverLetterAlert);
      return;
    }
    try {
      setIsGenerating(true);
      const blob = await pdf(
        <CoverLetterDocument 
          formData={formData} 
          coverLetterData={coverLetterData} 
          language={language} 
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Lettre_${formData.name.replace(/\s+/g, '_')}_${coverLetterData.companyName.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert(t.alertError);
    } finally {
      setIsGenerating(false);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="App">
      {/* === HEADER AVEC BOUTONS === */}
      <header className="app-header">
        <div className="header-content">
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
        <div className="header-buttons">
          {/* BOUTON LETTRE DE MOTIVATION */}
          <button 
            className="cover-letter-btn"
            onClick={() => setShowCoverLetterModal(true)}
            title={t.coverLetter}
          >
            <FiFileText className="cover-letter-icon" />
            <span>{t.coverLetter}</span>
          </button>

          {/* BOUTON LANGUE */}
          <button
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
            title={language === 'fr' ? 'Switch to English' : 'Passer au français'}
          >
            <FiGlobe className="lang-icon" />
            <span>{language === 'fr' ? 'FR' : 'EN'}</span>
          </button>

          {/* BOUTON À PROPOS */}
          <button className="about-btn" onClick={() => setShowAbout(true)}>
            {t.about}
          </button>
        </div>
      </header>

      {/* MODALE LETTRE DE MOTIVATION */}
      {showCoverLetterModal && (
        <div className="modal-overlay" onClick={() => setShowCoverLetterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCoverLetterModal(false)}>
              <FiX size={24} />
            </button>
            <div className="cover-letter-modal">
              <h2>{t.coverLetterTitle}</h2>
              
              <CoverLetterForm 
                coverLetterData={coverLetterData}
                setCoverLetterData={setCoverLetterData}
                language={language}
                t={t}
              />
              
              <div className="cover-letter-actions">
                <button 
                  onClick={handleDownloadCoverLetter} 
                  className="nav-btn download-btn"
                  disabled={isGenerating}
                >
                  {isGenerating ? t.generating : t.downloadCoverLetter}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === MODALE À PROPOS === */}
      {showAbout && (
        <div className="modal-overlay" onClick={() => setShowAbout(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAbout(false)}>
              <FiX size={24} />
            </button>
            <div className="about-card">
              <img src={cn} alt="Manel SIDAOUI" className="about-photo" />
              <h3>Manel SIDAOUI</h3>
              <p className="about-role">{t.role}</p>
              <p className="about-text">{t.aboutText}</p>
              <div className="about-links">
                <a href="https://www.linkedin.com/in/sidaoui-manel-904599289/" target="_blank" rel="noopener noreferrer">
                  {t.linkedin}
                </a>
                <a href="https://sidaouimanel.github.io/portfolio-2025/" target="_blank" rel="noopener noreferrer">
                  {t.portfolio}
                </a>
                <a href="mailto:sidaouimanel@gmail.com">{t.email}</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === CONTENU PRINCIPAL === */}
      <div className="app-container">
        <div className="progress-bar">
          {steps.map((step, index) => (
            <div key={step.number} className="step-indicator">
              <div className={`step-circle ${currentStep > step.number ? 'completed' : ''} ${currentStep === step.number ? 'active' : ''}`}>
                {currentStep > step.number ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                ) : step.number}
              </div>
              <span className="step-label">{step.title}</span>
              {index < steps.length - 1 && <div className="step-connector"></div>}
            </div>
          ))}
        </div>

        <div className="form-container">
          <div className="form-header">
            <h2>{steps[currentStep - 1].title}</h2>
          </div>

          <CurrentStepComponent formData={formData} setFormData={setFormData} language={language} />

          <div className="navigation-buttons">
            {currentStep > 1 && (
              <button onClick={prevStep} className="nav-btn prev-btn">
                {t.previous}
              </button>
            )}
            {currentStep < steps.length ? (
              <button onClick={nextStep} className="nav-btn next-btn">
                {t.next}
              </button>
            ) : (
              <button onClick={handleDownload} className="nav-btn download-btn" disabled={isGenerating}>
                {isGenerating ? t.generating : t.download}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* === FOOTER === */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-text">
            <p>
              {t.footerText} <FiHeart className="heart-icon" /> {t.by} Manel SIDAOUI
            </p>
            <p className="footer-rights">© 2025 {t.rights}</p>
          </div>
          <div className="footer-links">
            <a href="https://github.com/sidaouimanel" target="_blank" rel="noopener noreferrer" title="GitHub">
              <FiGithub className="footer-icon" />
            </a>
            <a href="https://www.linkedin.com/in/sidaoui-manel-904599289/" target="_blank" rel="noopener noreferrer" title="LinkedIn">
              <FiLinkedin className="footer-icon" />
            </a>
            <a href="mailto:sidaouimanel@gmail.com" title="Email">
              <FiMail className="footer-icon" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;